import Avatar from 'antd/lib/avatar/avatar';
import { FORMAT_TIME } from 'constants/days';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import moment from 'moment';
import React, { useMemo } from 'react';
import { IAppointment } from 'types/appointments';
import { GeneralFunction } from 'types/common';
import { IChatRoom } from 'types/messages';
import { ISignatory } from 'types/transaction';
import { getDefaultAvatarUrl } from 'utils';

import AppointmentCollapseDetail from '../AppointmentListInMessageTab/AppointmentCollapseDetail';
import LeaveAppointmentChat from '../AppointmentListInMessageTab/LeaveAppointmentChat';
import { checkUserIsInChatRoom } from '../hooks';

const AppointmentInfoModalContent: React.FC<{
  chatRoom?: IChatRoom;
  appointment?: IAppointment;
  signatories?: ISignatory[];
  refetchChatRoom?: GeneralFunction;
}> = ({ appointment, signatories, chatRoom, refetchChatRoom }) => {
  const userId = useCurrentLoggedInUser().id;
  const isUserInChat = checkUserIsInChatRoom(userId, chatRoom);
  const ChatRoomParticipantsComponent = useMemo(
    () =>
      chatRoom?.chatRelations?.reduce((acc, cR) => {
        const { user } = cR;
        if (!user) return acc;
        acc.push(
          <div key={user.id} className='flex items-center mt-2 first:mt-0'>
            <Avatar src={user.avatar?.thumbnailPipeFrom || getDefaultAvatarUrl(`${user.firstName} ${user.lastName}`)} />
            <div>
              {user.firstName} {user.lastName}
            </div>
          </div>,
        );
        return acc;
      }, [] as any[]),
    [chatRoom],
  );
  if (!appointment) return null;
  const signatoriesInAppointment = signatories?.filter((sig) => appointment.signatoryIds?.includes(sig.id)) || [];
  const appointmentTime = moment(appointment.date).format(FORMAT_TIME).toString();
  const { notary } = appointment;
  const appointmentRenderData = [
    {
      title: 'Time',
      children: appointmentTime,
    },
    {
      title: 'Signatories',
      children: signatoriesInAppointment.reduce(
        (acc, sig, index) =>
          index === signatoriesInAppointment.length - 1
            ? acc.concat(`${sig.firstName} ${sig.lastName}`)
            : acc.concat(`${sig.firstName} ${sig.lastName}, `),
        '',
      ),
    },
    {
      title: 'Notary',
      children: notary ? (
        <>
          <Avatar
            src={notary?.avatar?.thumbnailPipeFrom || getDefaultAvatarUrl(`${notary.firstName} ${notary.lastName}`)}
          />
          <div className='ml-2'>
            {notary.firstName} {notary.lastName}
          </div>
        </>
      ) : (
        <div>None</div>
      ),
    },
    {
      title: 'Participants',
      children: ChatRoomParticipantsComponent ? <div>{ChatRoomParticipantsComponent}</div> : <div>None</div>,
    },
  ];

  return (
    <div>
      {appointmentRenderData.map((data, index) => (
        <AppointmentCollapseDetail key={index} title={data.title}>
          {data.children}
        </AppointmentCollapseDetail>
      ))}
      {isUserInChat && <LeaveAppointmentChat callback={refetchChatRoom} chatRoom={chatRoom} />}
    </div>
  );
};

export default React.memo(AppointmentInfoModalContent);
